import { Injectable, signal, effect } from '@angular/core';
import { REMEMBERED_ENTRIES, STORAGE_KEYS } from '@shared/constants';
import { VideoProgress } from '@shared/models';

@Injectable({ providedIn: 'root' })
export class VideoPlayerService {
  /** signal holding all video progress */
  private progressMap = signal<Record<string, unknown>>(this.loadFromStorage());

  constructor() {
    /** Automatically persist whenever progress changes */
    effect(() => {
      localStorage.setItem(STORAGE_KEYS.VIDEO_PROGRESS, JSON.stringify(this.progressMap()));
    });
  }

  getProgress(videoId: string): VideoProgress | undefined {
    const saved = this.progressMap()[videoId];
    // Saved before the flag existed: resumed the way it always was.
    if (Number.isFinite(saved)) return { time: saved as number, interrupted: true };
    if (!saved || typeof saved !== 'object') return undefined;
    const { time, interrupted } = saved as Partial<VideoProgress>;
    return Number.isFinite(time) ? { time: time as number, interrupted: !!interrupted } : undefined;
  }

  /** Insertion order is recency: a video saved again moves to the end, the oldest drop off. */
  setProgress(videoId: string, progress: VideoProgress): void {
    this.progressMap.update((map) => {
      const newMap = { ...map };
      delete newMap[videoId];
      newMap[videoId] = progress;
      const ids = Object.keys(newMap);
      for (const oldest of ids.slice(0, Math.max(0, ids.length - REMEMBERED_ENTRIES))) {
        delete newMap[oldest];
      }
      return newMap;
    });
  }

  /** Remove saved timestamp */
  clearTime(videoId: string): void {
    this.progressMap.update((map) => {
      const newMap = { ...map };
      delete newMap[videoId];
      return newMap;
    });
  }

  /** Clear all videos */
  clearAll(): void {
    this.progressMap.set({});
  }

  private loadFromStorage(): Record<string, unknown> {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEYS.VIDEO_PROGRESS) ?? '{}');
      return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    } catch {
      return {};
    }
  }
}
